/**
 * JobCard Component
 * Displays a job listing in card format
 */

import { Card, CardContent, Box, Typography, Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BusinessIcon from '@mui/icons-material/Business';
import { Job } from '@services/jobsService';

interface JobCardProps {
  job: Job;
  onClick: () => void;
  showActions?: boolean;
}

export function JobCard({ job, onClick }: JobCardProps) {
  // Format salary display
  const formatSalary = () => {
    if (job.payRange) return job.payRange;
    if (job.salary_min && job.salary_max) {
      const type = job.salary_type === 'hourly' ? '/hr' : job.salary_type === 'daily' ? '/day' : '/yr';
      return `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}${type}`;
    }
    if (job.salary_min) {
      const type = job.salary_type === 'hourly' ? '/hr' : job.salary_type === 'daily' ? '/day' : '/yr';
      return `From $${job.salary_min.toLocaleString()}${type}`;
    }
    return null;
  };

  // Calculate days ago
  const getDaysAgo = () => {
    const posted = new Date(job.createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - posted.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const salary = formatSalary();

  return (
    <Card 
      onClick={onClick}
      sx={{
        backgroundColor: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(255,255,255,0.1)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.08)',
          borderColor: 'rgba(59, 130, 246, 0.3)',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header: Title and Type */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: 'white', 
              fontWeight: 600,
              lineHeight: 1.3,
              flex: 1,
              mr: 1
            }}
          >
            {job.title}
          </Typography>
          <Chip 
            label={job.jobType || 'Full-time'}
            size="small"
            sx={{
              backgroundColor: 'rgba(59, 130, 246, 0.2)',
              color: '#3b82f6',
              fontWeight: 500,
              fontSize: '0.7rem'
            }}
          />
        </Box>

        {/* Company */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <BusinessIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {job.company}
          </Typography>
        </Box>

        {/* Location */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
          <LocationOnIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 16 }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {job.location || job.county || job.basin || 'Location not specified'}
          </Typography>
        </Box>

        {/* Salary and Posted Date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
          {salary && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AttachMoneyIcon sx={{ color: '#22c55e', fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 600 }}>
                {salary}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }} />
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {getDaysAgo()}
            </Typography>
          </Box>
        </Box>

        {/* Required Certifications */}
        {job.required_certifications && job.required_certifications.length > 0 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {job.required_certifications.slice(0, 3).map((cert, i) => (
              <Chip 
                key={i}
                label={cert}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(245, 158, 11, 0.3)',
                  color: 'rgba(245, 158, 11, 0.9)',
                  fontSize: '0.65rem',
                  height: 20
                }}
              />
            ))}
            {job.required_certifications.length > 3 && (
              <Chip 
                label={`+${job.required_certifications.length - 3} more`}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: 'rgba(255,255,255,0.5)',
                  fontSize: '0.65rem',
                  height: 20
                }}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default JobCard;
